import { Editor } from "@tiptap/react";
import {
  BoldIcon,
  ItalicIcon,
  ListIcon,
  ListOrderedIcon,
  Undo2Icon,
  Redo2Icon,
} from "lucide-react";

function ToolbarButton(props: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      disabled={props.disabled}
      title={props.title}
      className={`p-1 rounded transition-colors ${
        props.isActive
          ? "bg-persian-100 text-persian-800"
          : "text-concrete-500 hover:text-concrete-800 hover:bg-concrete-50"
      } ${props.disabled ? "opacity-30 cursor-not-allowed" : ""}`}
    >
      {props.children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="w-px h-4 bg-concrete-200 mx-0.5" />;
}

const ICON = "w-3.5 h-3.5";

export function EditorToolbar(props: { editor: Editor }) {
  const { editor } = props;

  return (
    <div className="flex items-center gap-0.5 px-2 py-1 border-b border-concrete-100">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        isActive={editor.isActive("bold")}
        title="Bold (Cmd+B)"
      >
        <BoldIcon className={ICON} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        isActive={editor.isActive("italic")}
        title="Italic (Cmd+I)"
      >
        <ItalicIcon className={ICON} />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        isActive={editor.isActive("bulletList")}
        title="Bullet list"
      >
        <ListIcon className={ICON} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        isActive={editor.isActive("orderedList")}
        title="Ordered list"
      >
        <ListOrderedIcon className={ICON} />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (Cmd+Z)"
      >
        <Undo2Icon className={ICON} />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (Cmd+Shift+Z)"
      >
        <Redo2Icon className={ICON} />
      </ToolbarButton>
    </div>
  );
}
